import { IconButton, TextField, Tooltip } from '@material-ui/core';
import { CircleCheckIcon, EditIcon, SyncIcon } from 'components/Icons';
import CircleCloseIcon from 'components/Icons/CircleCloseIcon';
import CloseEyeIcon from 'components/Icons/CloseEyeIcon';
import EyeIcon from 'components/Icons/EyeIcon';
import TipoDocumento from 'models/TipoDocumento';
import React, { Fragment, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { assembleiasAssessorAlterarTipoDocumento, assembleiasAssessorDeletarTipoDocumento } from 'store/modules/AssembleiasAssessorActions';
import AxiosErrorHandler from 'utils/AxiosErrorHandler';
import TextRange from 'utils/TextRange';
import useStyle from './style';

export interface Props {
  loading: boolean;
  tipo: TipoDocumento;
  atualizar: Function;
}

const MAXLENGHT = 50;

export default({loading, tipo, atualizar}:Props ) => {
  const classes = useStyle();
  const [ editing, setEditing ] = useState<boolean>();
  const [ saving, setSaving ] = useState<boolean>();
  const [ enableDisable, setEnableDisable ] = useState<boolean>(false);
  const [ deleting, setDeleting ] = useState<boolean>();
  const [ descript, setDescript ] = useState<string>(tipo.descricao);
  const [ idCategoria, setIdCategoria ] = useState<number>(tipo.id);
  const [ flgDisabled, setFlgDisabled ] = useState<boolean>(tipo.flgDeleted);
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();

  useEffect(() => {
    setFlgDisabled(tipo.flgDeleted);
    setDescript(tipo.descricao);
    setIdCategoria(tipo.id);
  }, [tipo, setFlgDisabled, setDescript, setIdCategoria])

  const disabledButtons = useMemo(() => {
    return loading || saving || editing || enableDisable || deleting;
  }, [loading, saving, editing, enableDisable, deleting]);

  const limitText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescript(TextRange(event.currentTarget.value, MAXLENGHT));
  };

  const toSubmmitDeleting = useCallback(() => {
    setDeleting(true);
    reduxDispatch(
      assembleiasAssessorDeletarTipoDocumento(
        idCategoria,
        () => {
          atualizar();
          setDeleting(false);
        },
        error => {
          AxiosErrorHandler(error);
          setDeleting(false);
        }
      )
    )
    
  },[reduxDispatch, idCategoria, setDeleting, atualizar]);

  const toSubmmitEditing = useCallback((newFlgDisabled?: boolean): Promise<any> => {
    return new Promise((resolve: () => void, reject: (error: any) => void) => {
      reduxDispatch(
        assembleiasAssessorAlterarTipoDocumento(
          {
            descricao: descript,
            flgDeleted: typeof newFlgDisabled === 'boolean' ? newFlgDisabled : flgDisabled
          },
          idCategoria,
          () => {
            atualizar();
            resolve()
          },
          reject
        )
      )
    })
  },[reduxDispatch, descript, atualizar, idCategoria, flgDisabled]);

  const disableToggle = useCallback(() => {
    const newFlg: boolean = !flgDisabled;
    setEnableDisable(true);
    setFlgDisabled(newFlg);
    toSubmmitEditing(newFlg).then(
      () => {
        setEnableDisable(false)
      },
      error => {
        setFlgDisabled(!flgDisabled);
        setEnableDisable(false)
        AxiosErrorHandler(error);
      }
    )
  }, [setEnableDisable, setFlgDisabled, toSubmmitEditing, flgDisabled])

  const getWrapperButton = useCallback((disabled: boolean, tooltipText: string, contentRender: (disabled: boolean) => ReactElement): ReactNode => {
    if (disabled) {
      return (
        <Fragment>
          {contentRender(disabled)}
        </Fragment>
      )
    } else {
      return (
        <Tooltip title={tooltipText}>
          {contentRender(disabled)}
        </Tooltip>
      )
    }
  }, [])

  return (
    <TextField
      key={tipo.id}
      className={ classes.textField }
      onChange={limitText}
      value={descript}
      inputProps={{
        readOnly: !editing
      }}
      disabled={!!loading || !!enableDisable || !!deleting}
      InputProps={{
        endAdornment: (
          <Fragment>
            {
              getWrapperButton(
                !!loading || !!enableDisable || !!deleting,
                (editing ? `${ t("CategoriasEditModal.tooltips.save") }` : `${ t("CategoriasEditModal.tooltips.edit") }`),
                (disabled: boolean) => (
                  <IconButton
                    color="primary"
                    size="small" 
                    disabled={disabled}
                    onClick={() => {
                      if (!!editing) {
                        setSaving(true)
                        toSubmmitEditing().then(
                          () => {
                            setEditing(!editing)
                            setSaving(false)
                          },
                          error => {
                            setSaving(false)
                            AxiosErrorHandler(error);
                          }
                        )
                      } else {
                        setEditing(!editing)
                      }
                    }}
                  >
                    {
                      editing ? (
                        saving ? 
                          <SyncIcon animated />
                        : 
                        <CircleCheckIcon />
                      ) : (
                        <EditIcon />
                      )
                    }
                  </IconButton>
                )
              )
            }
            {
              getWrapperButton(
                !!disabledButtons,
                (flgDisabled ? `${ t("CategoriasEditModal.tooltips.disable") }` : `${ t("CategoriasEditModal.tooltips.enable") }`),
                (disabled: boolean) => (
                  <IconButton
                    color="primary"
                    size="small"
                    disabled={disabled}
                    onClick={() => {
                      disableToggle();
                    }}
                  >
                    {
                      enableDisable ? (
                        <SyncIcon animated />
                      ) : flgDisabled ? (
                          <CloseEyeIcon />
                        ) : (
                          <EyeIcon />
                        )
                    }
                  </IconButton>
                )
              )
            }
            {
              getWrapperButton(
                !!disabledButtons,
                t("CategoriasEditModal.tooltips.remove"),
                (disabled: boolean) => (
                  <IconButton size="small"
                    color="primary"
                    disabled={disabled}
                    onClick={() => {
                      toSubmmitDeleting();
                    }}
                  >
                    {
                      deleting ? (
                        <SyncIcon animated />
                      ) : (
                        <CircleCloseIcon />
                      )
                    }
                  </IconButton>
                )
              )
            }
          </Fragment>
        ),
      }}
    />
  )
}